/**
 * Room Manager - Handles room creation, joining, and listing via Supabase
 */

import { supabase, isSupabaseConfigured, supabaseRawFetch, getAccessToken } from '../lib/supabase';
import { authService } from '../auth';
import type { RoomInfo, RoomSettings, RoomStatus } from './types';

/** Default room settings */
const DEFAULT_ROOM_SETTINGS: RoomSettings = {
  isPublic: true,
  boardSize: 5,
  maxSpectators: 10,
};

/** Characters used for room code generation */
const ROOM_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const ROOM_CODE_LENGTH = 6;

/**
 * Generate a random 6-character alphanumeric room code
 * Excludes easily confused characters (0, O, 1, I)
 */
export function generateRoomCode(): string {
  let code = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    code += ROOM_CODE_CHARS.charAt(
      Math.floor(Math.random() * ROOM_CODE_CHARS.length)
    );
  }
  return code;
}

/**
 * Room Manager class for handling multiplayer rooms
 */
export class RoomManager {
  private partyKitHost: string;

  constructor(partyKitHost?: string) {
    // Default to environment variable or production PartyKit host
    this.partyKitHost =
      partyKitHost ||
      (typeof window !== 'undefined'
        ? (window as unknown as { PARTYKIT_HOST?: string }).PARTYKIT_HOST
        : undefined) ||
      import.meta.env?.VITE_PARTYKIT_HOST ||
      'path-game.huang-jiaming.partykit.dev';
  }

  /**
   * Create a new room
   * Uses raw fetch to avoid Supabase client auth state issues
   * @throws Error if room creation fails (no silent fallback to local rooms)
   */
  async createRoom(
    settings: Partial<RoomSettings> = {},
    hostId?: string
  ): Promise<RoomInfo> {
    const roomSettings: RoomSettings = {
      ...DEFAULT_ROOM_SETTINGS,
      ...settings,
    };

    // Generate a unique room code
    const roomCode = generateRoomCode();

    // If Supabase is not configured, create a local-only room with a warning
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured - room will only work locally');
      return {
        id: crypto.randomUUID(),
        code: roomCode,
        hostId: hostId || '',
        status: 'waiting',
        playerCount: 0,
        settings: roomSettings,
        createdAt: new Date().toISOString(),
        isLocalOnly: true,
      } as RoomInfo & { isLocalOnly: boolean };
    }

    console.debug('[RoomManager] Creating room via raw fetch...');
    const startTime = Date.now();

    try {
      // Wait for auth to finish loading if it's still in progress
      if (authService.isLoading()) {
        console.debug('[RoomManager] Waiting for auth to complete...');
        await new Promise<void>((resolve) => {
          const unsubscribe = authService.onAuthStateChange(() => {
            if (!authService.isLoading()) {
              unsubscribe();
              resolve();
            }
          });
          // Timeout after 5 seconds
          setTimeout(() => {
            unsubscribe();
            resolve();
          }, 5000);
        });
      }

      // Get the current access token for authenticated request
      const accessToken = await getAccessToken();
      if (!accessToken) {
        // Double check if user appears to be signed in from cache
        const user = authService.getCurrentUser();
        if (user) {
          console.warn('[RoomManager] User appears signed in but no access token available');
          throw new Error('Session issue detected. Please refresh the page or sign in again.');
        }
        throw new Error('You must be signed in to create a room. Please sign in first.');
      }

      // Use raw fetch to bypass Supabase client auth issues
      const { data, error } = await supabaseRawFetch<Array<{
        id: string;
        room_code: string;
        host_id: string;
        status: string;
        player_count: number;
        settings: RoomSettings;
        created_at: string;
      }>>('rooms', {
        method: 'POST',
        body: {
          room_code: roomCode,
          host_id: hostId,
          status: 'waiting' as RoomStatus,
          player_count: 0,
          settings: roomSettings,
          partykit_room_id: roomCode,
        },
        accessToken,
      });

      console.debug(`[RoomManager] Room creation completed in ${Date.now() - startTime}ms`);

      if (error) {
        console.error('Room creation error:', error);
        // Provide user-friendly error messages
        if (error.includes('42501') || error.includes('policy')) {
          throw new Error('You must be signed in to create a room. Please sign in again.');
        }
        if (error.includes('23505')) {
          // Duplicate room code (very rare) - try again with new code
          return this.createRoom(settings, hostId);
        }
        if (error.includes('JWT') || error.includes('401')) {
          throw new Error('Session expired. Please sign in again.');
        }
        throw new Error(`Failed to create room: ${error}`);
      }

      // Raw fetch returns an array, get the first item
      const roomData = Array.isArray(data) ? data[0] : data;
      
      if (!roomData) {
        throw new Error('Room creation failed - no data returned');
      }

      return {
        id: roomData.id,
        code: roomData.room_code,
        hostId: roomData.host_id,
        status: roomData.status as RoomStatus,
        playerCount: roomData.player_count,
        settings: roomData.settings as RoomSettings,
        createdAt: roomData.created_at,
      };
    } catch (error) {
      console.error('Room creation failed:', error);
      
      // Re-throw errors with user-friendly messages
      throw error instanceof Error ? error : new Error('Failed to create room');
    }
  }

  /**
   * Join an existing room by code
   * Uses raw fetch to avoid Supabase client auth state issues
   */
  async joinRoom(code: string): Promise<RoomInfo | null> {
    const normalizedCode = code.toUpperCase().trim();

    if (!isSupabaseConfigured()) {
      // Return mock room for development
      return {
        id: crypto.randomUUID(),
        code: normalizedCode,
        hostId: '',
        status: 'waiting',
        playerCount: 1,
        settings: DEFAULT_ROOM_SETTINGS,
        createdAt: new Date().toISOString(),
      };
    }

    console.debug(`[RoomManager] Joining room ${normalizedCode} via raw fetch...`);

    // Get access token to see all rooms (including private ones)
    const accessToken = await getAccessToken();

    const { data, error } = await supabaseRawFetch<Array<{
      id: string;
      room_code: string;
      host_id: string;
      status: string;
      player_count: number;
      settings: RoomSettings;
      created_at: string;
    }>>(`rooms?room_code=eq.${normalizedCode}`, {
      accessToken: accessToken || undefined,
    });

    if (error || !data || data.length === 0) {
      console.debug('[RoomManager] Room not found');
      return null;
    }

    const roomData = data[0];

    // Check if room is joinable
    if (roomData.status !== 'waiting') {
      throw new Error('Room is not available for joining');
    }

    if (roomData.player_count >= 2) {
      throw new Error('Room is full');
    }

    return {
      id: roomData.id,
      code: roomData.room_code,
      hostId: roomData.host_id,
      status: roomData.status as RoomStatus,
      playerCount: roomData.player_count,
      settings: roomData.settings as RoomSettings,
      createdAt: roomData.created_at,
    };
  }

  /**
   * List public rooms that are waiting for players
   * Uses raw fetch to avoid Supabase client auth state issues
   */
  async listPublicRooms(): Promise<RoomInfo[]> {
    if (!isSupabaseConfigured()) {
      // Return empty list for development
      return [];
    }

    console.debug('[RoomManager] Listing public rooms via raw fetch...');
    const startTime = Date.now();

    try {
      // Get access token so authenticated users can see all rooms
      const accessToken = await getAccessToken();

      // Use raw fetch to bypass Supabase client auth issues
      const { data, error } = await supabaseRawFetch<Array<{
        id: string;
        room_code: string;
        host_id: string;
        status: string;
        player_count: number;
        settings: RoomSettings;
        created_at: string;
      }>>('rooms?status=eq.waiting&player_count=lt.2&order=created_at.desc&limit=20', {
        accessToken: accessToken || undefined,
      });
      
      console.debug(`[RoomManager] Room list query completed in ${Date.now() - startTime}ms`);

      if (error) {
        console.error('Error listing rooms:', error);
        return [];
      }

      // Filter for public rooms (settings->>isPublic = 'true')
      return (data || [])
        .filter((room) => {
          const settings = room.settings as RoomSettings;
          return settings?.isPublic !== false;
        })
        .map((room) => ({
          id: room.id,
          code: room.room_code,
          hostId: room.host_id,
          status: room.status as RoomStatus,
          playerCount: room.player_count,
          settings: room.settings as RoomSettings,
          createdAt: room.created_at,
        }));
    } catch (error) {
      console.warn('Error listing rooms (timeout or network):', error);
      return [];
    }
  }

  /**
   * Get room information by code
   * Uses raw fetch to avoid Supabase client auth state issues
   */
  async getRoomInfo(code: string): Promise<RoomInfo | null> {
    const normalizedCode = code.toUpperCase().trim();

    if (!isSupabaseConfigured()) {
      return null;
    }

    // Get access token to see all rooms (including private ones)
    const accessToken = await getAccessToken();

    const { data, error } = await supabaseRawFetch<Array<{
      id: string;
      room_code: string;
      host_id: string;
      status: string;
      player_count: number;
      settings: RoomSettings;
      created_at: string;
    }>>(`rooms?room_code=eq.${normalizedCode}`, {
      accessToken: accessToken || undefined,
    });

    if (error || !data || data.length === 0) {
      return null;
    }

    const roomData = data[0];

    return {
      id: roomData.id,
      code: roomData.room_code,
      hostId: roomData.host_id,
      status: roomData.status as RoomStatus,
      playerCount: roomData.player_count,
      settings: roomData.settings as RoomSettings,
      createdAt: roomData.created_at,
    };
  }

  /**
   * Update room status
   */
  async updateRoomStatus(code: string, status: RoomStatus): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) {
      return true;
    }

    const { error } = await supabase
      .from('rooms')
      .update({ status })
      .eq('room_code', code.toUpperCase().trim());

    return !error;
  }

  /**
   * Update room player count
   */
  async updatePlayerCount(code: string, count: number): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) {
      return true;
    }

    const { error } = await supabase
      .from('rooms')
      .update({ player_count: count })
      .eq('room_code', code.toUpperCase().trim());

    return !error;
  }

  /**
   * Delete a room
   */
  async deleteRoom(code: string): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) {
      return true;
    }

    const { error } = await supabase
      .from('rooms')
      .delete()
      .eq('room_code', code.toUpperCase().trim());

    return !error;
  }

  /**
   * Get the PartyKit host URL
   */
  getPartyKitHost(): string {
    return this.partyKitHost;
  }
}

/** Default room manager instance */
export const roomManager = new RoomManager();
